import { useEffect, useState } from 'react';
import { LocalStorageService } from '../services/storage/LocalStorageService';
import { DsBuilderData, DS_BUILDER_STORAGE_KEY } from '../types/localStorage';
import { ThemeKey } from '@common/constants/themes';
import { setMsIdAtom, useMsIdAtomValue, useCurrentCategoryValue, useSetCurrentCategoryValue } from '../context';
import {
  useSelectedThemeColor,
  useSetSelectedThemeColor,
} from '../features/templateSelection/context/templateContext';

type SyncOptions = {
  expirationHours?: number;
  syncKeys?: (keyof DsBuilderData)[];
};

export const useLocalStorageSync = (options?: SyncOptions) => {
  const { expirationHours, syncKeys } = options || {};
  const [isLoading, setIsLoading] = useState(true);
  const msId = useMsIdAtomValue();
  const currentCategoryValue = useCurrentCategoryValue();
  const setCurrentCategoryValue = useSetCurrentCategoryValue();
  const selectedThemeColor = useSelectedThemeColor();
  const setSelectedThemeColor = useSetSelectedThemeColor();
  const localStorageService = new LocalStorageService();

  const STORAGE_KEY = DS_BUILDER_STORAGE_KEY;

  useEffect(() => {
    const syncData = async () => {
      try {
        const updates: Partial<DsBuilderData> = {};

        if ((!syncKeys || syncKeys.includes('msId')) && msId) {
          updates.msId = msId;
        }

        if ((!syncKeys || syncKeys.includes('selectedCategory')) && currentCategoryValue) {
          updates.selectedCategory = currentCategoryValue;
        }

        if ((!syncKeys || syncKeys.includes('selectedThemeColor')) && selectedThemeColor) {
          updates.selectedThemeColor = selectedThemeColor;
        }

        if (Object.keys(updates).length > 0) {
          await localStorageService.updateObjectKey<DsBuilderData>(STORAGE_KEY, updates, expirationHours);
        }

        await restoreFromStorage();
      } finally {
        setIsLoading(false);
      }
    };

    const restoreFromStorage = async () => {
      const storedData = await localStorageService.getObject<DsBuilderData>(STORAGE_KEY);

      if (!msId && storedData?.msId) {
        setMsIdAtom(storedData.msId);
      }

      if (!currentCategoryValue && storedData?.selectedCategory) {
        setCurrentCategoryValue(storedData.selectedCategory);
      }

      if (!selectedThemeColor && storedData?.selectedThemeColor) {
        setSelectedThemeColor(storedData.selectedThemeColor as ThemeKey);
      }
    };

    syncData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { isLoading };
};
